import {
    ManagedAgentResponseModel,
    MgmtApi
} from '@/customApiClients/mgmtApiClient/Api';
import { ChevronRight, Loader2, Plug, Plus, TriangleAlert, Vault } from 'lucide-react';
import React, { useEffect, useId, useState } from 'react';
import { toast } from 'sonner';

import { ConfirmDeleteDialog } from '@/components/ConfirmDeleteDialog';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SectionHeader } from '@/components/ui/section-header';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/ui/select';
import { SettingRow } from '@/components/ui/setting-row';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle
} from '@/components/ui/sheet';
import { StatusBadge, type StatusTone } from '@/components/ui/status-badge';
import { Switch } from '@/components/ui/switch';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@/components/ui/table';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { SubtleTabsList } from '@/components/ui/tabs-subtle';

import { useAuth } from '@/hooks/useAuth';

interface Props {
    mgmtApi: MgmtApi<unknown>;
}

interface VaultItem {
    id: string;
    name: string;
    organization?: string | null;
}

type AuthMethod = 'client_secret_basic' | 'client_secret_post' | 'none';

interface Credential {
    credentialId: string;
    displayName: string;
    type: string;
    mcpServerUrl?: string | null;
    secretName?: string | null;
    expiresAt?: string | null;
    hasRefresh: boolean;
    clientId?: string | null;
    tokenEndpoint?: string | null;
    tokenEndpointAuth?: AuthMethod | null;
    scope?: string | null;
    inCurrentEnvironment: boolean;
    createdAt?: string | null;
    updatedAt?: string | null;
}

interface McpCoverage {
    name: string;
    displayName: string;
    description: string;
    isHidden: boolean;
    mcpServerUrl: string;
    credentialId?: string | null;
    credentialName?: string | null;
}

interface ProbeHttp {
    statusCode: number;
    contentType: string;
    body: string;
    bodyTruncated: boolean;
}

interface Validation {
    status: 'valid' | 'invalid' | 'unknown';
    hasRefreshToken: boolean;
    refreshStatus?: 'succeeded' | 'failed' | 'connect_error' | 'no_refresh_token' | null;
    refreshResponse?: ProbeHttp | null;
    mcpProbeMethod?: string | null;
    mcpProbeResponse?: ProbeHttp | null;
    validatedAt?: string | null;
}

type Health = 'valid' | 'invalid' | 'expired' | 'unchecked' | 'ok';

const HEALTH_LABEL: Record<Health, string> = {
    valid: 'Valid',
    invalid: 'Invalid',
    expired: 'Expired',
    unchecked: 'Token expired – validate',
    ok: 'OK'
};

const HEALTH_TONE: Record<Health, StatusTone> = {
    valid: 'success',
    ok: 'success',
    unchecked: 'warning',
    invalid: 'danger',
    expired: 'danger'
};

const AUTH_METHOD_LABEL: Record<AuthMethod, string> = {
    client_secret_basic: 'Client secret (HTTP Basic)',
    client_secret_post: 'Client secret (POST body)',
    none: 'None (public client)'
};

const healthOf = (c: Credential, v?: Validation): Health => {
    if (v?.status === 'valid') return 'valid';
    if (v?.status === 'invalid') return 'invalid';
    if (c.expiresAt && new Date(c.expiresAt).getTime() <= Date.now()) {
        return c.hasRefresh ? 'unchecked' : 'expired';
    }
    return 'ok';
};

const fixHint = (c: Credential, v?: Validation): string | null => {
    if (v) {
        const refreshBody = v.refreshResponse?.body ?? '';
        if (v.refreshStatus === 'failed' && refreshBody.includes('invalid_grant'))
            return 'The refresh token was rejected — its Keycloak session has expired or been revoked. Replace the tokens with a fresh pair.';
        if (
            v.refreshStatus === 'failed' &&
            (refreshBody.includes('invalid_client') || v.refreshResponse?.statusCode === 401)
        )
            return 'The token endpoint rejected the client. Update the client secret or switch the auth method (Basic vs POST).';
        if (v.refreshStatus === 'failed')
            return 'The refresh exchange failed. See the provider response below.';
        if (v.refreshStatus === 'connect_error')
            return 'The token endpoint could not be reached. The endpoint cannot be changed — archive this credential and create a new one.';
        if (v.status === 'invalid' && v.refreshStatus === 'succeeded')
            return 'A new token was issued, but the MCP server rejected it. Check the audience and roles the MCP server expects.';
        if (v.status === 'invalid' && v.refreshStatus === 'no_refresh_token')
            return 'There is no refresh token, so the expired access token cannot be renewed. Replace the tokens.';
        if (v.status === 'invalid' && v.mcpProbeResponse?.statusCode === 401)
            return v.refreshStatus
                ? 'The MCP server rejected the access token (401). Check the provider response above.'
                : 'The MCP server rejected the access token (401) and no refresh was attempted, so the stored token is used as-is. Replace the tokens — and note that without a working refresh it will expire again.';
        return null;
    }
    if (healthOf(c) === 'unchecked') return 'Run Validate to see whether auto-refresh still works.';
    if (healthOf(c) === 'expired') return 'The access token has expired and there is no refresh token. Replace the tokens.';
    return null;
};

const fmtDateTime = (iso?: string | null) =>
    iso ? new Date(iso).toLocaleString() : '—';

const toIso = (local: string) => (local ? new Date(local).toISOString() : null);

const readError = async (res: Response, fallback: string) => {
    try {
        return (await res.json())?.detail || fallback;
    } catch {
        return fallback;
    }
};

const emptyWizard = {
    mcpServerUrl: '',
    displayName: '',
    accessToken: '',
    expiresAt: '',
    withRefresh: true,
    clientId: '',
    clientSecret: '',
    tokenEndpoint: '',
    authMethod: 'client_secret_basic' as AuthMethod,
    refreshToken: '',
    scope: ''
};

interface FieldProps {
    label: string;
    hint?: string;
    htmlFor?: string;
    children: React.ReactNode;
}

const Field: React.FC<FieldProps> = ({ label, hint, htmlFor, children }) => {
    const generatedId = useId();
    const id = htmlFor ?? generatedId;
    return (
        <div className="space-y-1.5">
            <Label htmlFor={id}>{label}</Label>
            {React.isValidElement<{ id?: string }>(children) &&
            children.type === Input
                ? React.cloneElement(children, { id })
                : children}
            {hint && <p className="text-caption text-muted-foreground">{hint}</p>}
        </div>
    );
};

const FormActions: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="flex justify-end gap-2 pt-1">{children}</div>
);

interface CredentialTableRow {
    key: string;
    name: string;
    url?: string | null;
    health?: Health;
    onOpen?: () => void;
    onAdd?: () => void;
    adding?: boolean;
}

const CredentialTable: React.FC<{ rows: CredentialTableRow[] }> = ({ rows }) => (
    <Table variant="fluid">
        <TableHeader>
            <TableRow>
                <TableHead>Server</TableHead>
                <TableHead className="w-64">Status</TableHead>
                <TableHead className="w-px" />
            </TableRow>
        </TableHeader>
        <TableBody>
            {rows.map((row) => (
                <TableRow key={row.key} className={row.onOpen ? 'cursor-pointer' : undefined}>
                    <TableCell className="max-w-0 w-full">
                        {row.onOpen ? (
                            <button
                                type="button"
                                onClick={row.onOpen}
                                className="block w-full font-medium text-foreground text-left truncate outline-none after:absolute after:inset-0 focus-visible:after:ring-1 focus-visible:after:ring-focus-ring"
                            >
                                {row.name}
                            </button>
                        ) : (
                            <div className="font-medium text-foreground truncate">{row.name}</div>
                        )}
                        <div className="font-mono text-caption text-muted-foreground truncate">
                            {row.url}
                        </div>
                    </TableCell>
                    <TableCell>
                        {row.health ? (
                            <StatusBadge tone={HEALTH_TONE[row.health]}>
                                {HEALTH_LABEL[row.health]}
                            </StatusBadge>
                        ) : (
                            <StatusBadge tone="warning">Missing credential</StatusBadge>
                        )}
                    </TableCell>
                    <TableCell className="text-right">
                        {row.onOpen ? (
                            <ChevronRight className="ml-auto size-4" />
                        ) : (
                            <Button
                                variant="tertiary"
                                disabled={row.adding}
                                onClick={row.onAdd}
                            >
                                {row.adding ? <Loader2 className="animate-spin" /> : <Plus />}
                                Add
                            </Button>
                        )}
                    </TableCell>
                </TableRow>
            ))}
        </TableBody>
    </Table>
);

export const VaultsTab: React.FC<Props> = ({ mgmtApi }) => {
    const { user } = useAuth();
    const myOrganization = user?.metadata?.organization || '';

    const [vaults, setVaults] = useState<VaultItem[]>([]);
    const [dbAgents, setDbAgents] = useState<ManagedAgentResponseModel[]>([]);
    const [vaultId, setVaultId] = useState('');
    const [loadingVaults, setLoadingVaults] = useState(false);

    const [credentials, setCredentials] = useState<Credential[]>([]);
    const [coverage, setCoverage] = useState<McpCoverage[]>([]);
    const [addingMcp, setAddingMcp] = useState<string | null>(null);
    const [loadingCredentials, setLoadingCredentials] = useState(false);
    const [validations, setValidations] = useState<Record<string, Validation>>({});
    const [validating, setValidating] = useState<string | null>(null);

    const [inspectId, setInspectId] = useState<string | null>(null);
    const [editMode, setEditMode] = useState<'secret' | 'tokens' | null>(null);
    const [secretForm, setSecretForm] = useState({
        clientSecret: '',
        authMethod: 'client_secret_basic' as Exclude<AuthMethod, 'none'>
    });
    const [tokenForm, setTokenForm] = useState({
        accessToken: '',
        expiresAt: '',
        refreshToken: ''
    });
    const [archiveTarget, setArchiveTarget] = useState<Credential | null>(null);
    const [busy, setBusy] = useState(false);

    const [wizardStep, setWizardStep] = useState<0 | 1 | 2 | 3>(0);
    const [wizard, setWizard] = useState(emptyWizard);

    const baseUrl = `/api/vaults/${encodeURIComponent(vaultId)}/credentials`;

    useEffect(() => {
        setLoadingVaults(true);
        fetch('/api/vaults', { credentials: 'include' })
            .then((res) => (res.ok ? res.json() : []))
            .then((data: VaultItem[]) => {
                const visible = (Array.isArray(data) ? data : []).filter(
                    (v) => !v.organization || v.organization === myOrganization
                );
                setVaults(visible);
                setVaultId((current) => current || visible[0]?.id || '');
            })
            .catch(() => {
                setVaults([]);
                toast.error('Failed to load vaults');
            })
            .finally(() => setLoadingVaults(false));
        mgmtApi.api
            .v1ManagedAgentsList()
            .then((res) => setDbAgents(res?.data?.data || []))
            .catch(() => setDbAgents([]));
    }, [mgmtApi, myOrganization]);

    const fetchCredentials = () => {
        if (!vaultId) return;
        setLoadingCredentials(true);
        fetch(baseUrl, { credentials: 'include' })
            .then(async (res) => {
                if (!res.ok) throw new Error(await readError(res, 'Failed to load credentials'));
                return res.json();
            })
            .then((data) => setCredentials(Array.isArray(data) ? data : []))
            .catch((e) => {
                setCredentials([]);
                toast.error(e.message);
            })
            .finally(() => setLoadingCredentials(false));
        fetch(`/api/vaults/${encodeURIComponent(vaultId)}/mcp-coverage`, {
            credentials: 'include'
        })
            .then((res) => (res.ok ? res.json() : []))
            .then((data) => setCoverage(Array.isArray(data) ? data : []))
            .catch(() => setCoverage([]));
    };

    const addManagedCredential = async (mcp: McpCoverage) => {
        setAddingMcp(mcp.name);
        const created = await request(
            `${baseUrl}/managed`,
            'POST',
            { mcpServerUrl: mcp.mcpServerUrl, displayName: mcp.displayName },
            'Failed to add credential'
        );
        setAddingMcp(null);
        if (created) {
            toast.success(`Credential added for ${mcp.displayName}`);
            fetchCredentials();
        }
    };

    useEffect(() => {
        setCredentials([]);
        setInspectId(null);
        fetchCredentials();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vaultId]);

    const request = async (
        url: string,
        method: 'POST' | 'PATCH',
        body: unknown,
        fallback: string
    ) => {
        setBusy(true);
        try {
            const res = await fetch(url, {
                method,
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: body === undefined ? undefined : JSON.stringify(body)
            });
            if (!res.ok) throw new Error(await readError(res, fallback));
            return await res.json();
        } catch (e: any) {
            toast.error(e.message);
            return null;
        } finally {
            setBusy(false);
        }
    };

    const validate = async (credentialId: string) => {
        setValidating(credentialId);
        try {
            const res = await fetch(
                `${baseUrl}/${encodeURIComponent(credentialId)}/validate`,
                { method: 'POST', credentials: 'include' }
            );
            if (!res.ok) throw new Error(await readError(res, 'Validation failed'));
            const result: Validation = await res.json();
            setValidations((v) => ({ ...v, [credentialId]: result }));
            fetchCredentials();
        } catch (e: any) {
            toast.error(e.message);
        } finally {
            setValidating(null);
        }
    };

    const saveSecret = async (c: Credential) => {
        if (!secretForm.clientSecret) {
            toast.error('Client secret is required');
            return;
        }
        const ok = await request(
            `${baseUrl}/${encodeURIComponent(c.credentialId)}`,
            'PATCH',
            { clientSecret: secretForm.clientSecret, authMethod: secretForm.authMethod },
            'Failed to update client secret'
        );
        if (ok) {
            toast.success('Client secret updated — validating');
            setEditMode(null);
            validate(c.credentialId);
        }
    };

    const saveTokens = async (c: Credential) => {
        if (!tokenForm.accessToken && !tokenForm.refreshToken) {
            toast.error('Enter an access token and/or a refresh token');
            return;
        }
        const ok = await request(
            `${baseUrl}/${encodeURIComponent(c.credentialId)}`,
            'PATCH',
            {
                accessToken: tokenForm.accessToken || null,
                expiresAt: toIso(tokenForm.expiresAt),
                refreshToken: tokenForm.refreshToken || null
            },
            'Failed to replace tokens'
        );
        if (ok) {
            toast.success('Tokens replaced — validating');
            setEditMode(null);
            validate(c.credentialId);
        }
    };

    const archive = async () => {
        if (!archiveTarget) return;
        const ok = await request(
            `${baseUrl}/${encodeURIComponent(archiveTarget.credentialId)}/archive`,
            'POST',
            undefined,
            'Failed to archive credential'
        );
        if (ok) {
            toast.success('Credential archived');
            setInspectId(null);
            fetchCredentials();
        }
        setArchiveTarget(null);
    };

    const createCredential = async () => {
        const created = await request(
            baseUrl,
            'POST',
            {
                mcpServerUrl: wizard.mcpServerUrl,
                displayName: wizard.displayName || null,
                accessToken: wizard.accessToken,
                expiresAt: toIso(wizard.expiresAt),
                refresh: wizard.withRefresh
                    ? {
                          clientId: wizard.clientId,
                          clientSecret: wizard.authMethod === 'none' ? null : wizard.clientSecret,
                          tokenEndpoint: wizard.tokenEndpoint,
                          authMethod: wizard.authMethod,
                          refreshToken: wizard.refreshToken,
                          scope: wizard.scope || null
                      }
                    : null
            },
            'Failed to create credential'
        );
        if (created) {
            toast.success('Credential created');
            setWizard(emptyWizard);
            setWizardStep(0);
            fetchCredentials();
        }
    };

    const openEdit = (mode: 'secret' | 'tokens', c: Credential) => {
        setEditMode(mode);
        setSecretForm({
            clientSecret: '',
            authMethod:
                c.tokenEndpointAuth === 'client_secret_post'
                    ? 'client_secret_post'
                    : 'client_secret_basic'
        });
        setTokenForm({ accessToken: '', expiresAt: '', refreshToken: '' });
    };

    const vault = vaults.find((v) => v.id === vaultId);
    const usedBy = dbAgents
        .filter((a) => a.vaultId === vaultId)
        .map((a) => (a.isCoordinator ? `${a.displayName} (coordinator)` : a.displayName));
    const visibleCredentials = credentials.filter((c) => c.inCurrentEnvironment);
    const coveredIds = new Set(coverage.map((m) => m.credentialId).filter(Boolean));
    const otherCredentials = visibleCredentials.filter((c) => !coveredIds.has(c.credentialId));
    const missingCount = coverage.filter((m) => !m.credentialId).length;
    const inspected = credentials.find((c) => c.credentialId === inspectId);
    const inspectedValidation = inspected ? validations[inspected.credentialId] : undefined;

    const wizardStep1Valid = !!wizard.mcpServerUrl.trim();
    const wizardStep2Valid =
        !!wizard.accessToken &&
        (!wizard.withRefresh ||
            (!!wizard.clientId &&
                !!wizard.tokenEndpoint &&
                !!wizard.refreshToken &&
                (wizard.authMethod === 'none' || !!wizard.clientSecret)));

    const openCredential = (credentialId: string) => () => {
        setInspectId(credentialId);
        setEditMode(null);
    };

    const coverageRows: CredentialTableRow[] = coverage.map((mcp) => {
        const cred = credentials.find((c) => c.credentialId === mcp.credentialId);
        if (!cred) {
            return {
                key: mcp.name,
                name: mcp.displayName,
                url: mcp.mcpServerUrl,
                onAdd: () => addManagedCredential(mcp),
                adding: addingMcp === mcp.name
            };
        }
        return {
            key: mcp.name,
            name: mcp.displayName,
            url: mcp.mcpServerUrl,
            health: healthOf(cred, validations[cred.credentialId]),
            onOpen: openCredential(cred.credentialId)
        };
    });

    const otherRows: CredentialTableRow[] = otherCredentials.map((c) => ({
        key: c.credentialId,
        name: c.displayName,
        url: c.mcpServerUrl || c.secretName,
        health: healthOf(c, validations[c.credentialId]),
        onOpen: openCredential(c.credentialId)
    }));

    const inspectedHint = inspected ? fixHint(inspected, inspectedValidation) : null;

    return (
        <section className="space-y-6">
            <SectionHeader
                title="Vaults"
                description="MCP OAuth credentials stored by Anthropic. Agents get them through the vault selected on the agent."
            />

            {loadingVaults ? (
                <TableSkeleton columns={3} rows={4} />
            ) : vaults.length === 0 ? (
                <EmptyState
                    icon={Vault}
                    title="No vaults found"
                    description="Vaults created in your Anthropic workspace show up here"
                />
            ) : (
                <div className="space-y-6">
                    {vaults.length > 1 && (
                        <div className="pb-2">
                            <SubtleTabsList
                                tabs={vaults.map((v) => ({ value: v.id, label: v.name }))}
                                value={vaultId}
                                onValueChange={setVaultId}
                            />
                        </div>
                    )}

                    <p className="text-muted-foreground text-subtitle">
                        {vaults.length === 1 && (
                            <span className="font-medium text-foreground">{vault?.name} · </span>
                        )}
                        {usedBy.length
                            ? `Used by ${usedBy.join(', ')} — changes apply to all of them.`
                            : 'No agents use this vault yet.'}
                    </p>

                    {loadingCredentials ? (
                        <TableSkeleton columns={3} rows={4} />
                    ) : (
                        <div className="space-y-10">
                            <section className="space-y-3">
                                <SectionHeader
                                    title="Organization MCP servers"
                                    description={
                                        coverage.length === 0
                                            ? 'Servers available to your organization.'
                                            : missingCount
                                              ? `${missingCount} of ${coverage.length} servers are missing a credential.`
                                              : `All ${coverage.length} servers have a credential.`
                                    }
                                    action={
                                        <Button onClick={() => setWizardStep(1)}>
                                            <Plus /> New credential
                                        </Button>
                                    }
                                />
                                {coverage.length === 0 ? (
                                    <EmptyState
                                        icon={Plug}
                                        title="No MCP servers available"
                                        description="No MCP servers are available to your organization"
                                        className="py-8"
                                    />
                                ) : (
                                    <CredentialTable rows={coverageRows} />
                                )}
                            </section>
                            {otherRows.length > 0 && (
                                <section className="space-y-3">
                                    <SectionHeader
                                        title="Other credentials"
                                        description="Not linked to an organization MCP server."
                                    />
                                    <CredentialTable rows={otherRows} />
                                </section>
                            )}
                        </div>
                    )}
                </div>
            )}

            <Sheet open={!!inspected} onOpenChange={(o) => !o && setInspectId(null)}>
                <SheetContent className="space-y-6 sm:max-w-lg overflow-y-auto">
                    {inspected && (
                        <>
                            <SheetHeader>
                                <SheetTitle>{inspected.displayName}</SheetTitle>
                                <SheetDescription className="font-mono break-all">
                                    {inspected.mcpServerUrl || inspected.secretName}
                                </SheetDescription>
                            </SheetHeader>

                            <StatusBadge tone={HEALTH_TONE[healthOf(inspected, inspectedValidation)]}>
                                {HEALTH_LABEL[healthOf(inspected, inspectedValidation)]}
                            </StatusBadge>

                            <dl className="gap-x-6 gap-y-2 grid grid-cols-[auto_1fr] text-subtitle">
                                <dt className="text-muted-foreground">Type</dt>
                                <dd className="text-foreground">{inspected.type}</dd>
                                <dt className="text-muted-foreground">Access token expires</dt>
                                <dd className="text-foreground">
                                    {inspected.expiresAt ? fmtDateTime(inspected.expiresAt) : 'Not set'}
                                </dd>
                                <dt className="text-muted-foreground">Auto-refresh</dt>
                                <dd className="text-foreground">{inspected.hasRefresh ? 'Configured' : 'None'}</dd>
                                {inspected.hasRefresh && (
                                    <>
                                        <dt className="text-muted-foreground">Client ID</dt>
                                        <dd className="font-mono text-foreground break-all">{inspected.clientId}</dd>
                                        <dt className="text-muted-foreground">Token endpoint</dt>
                                        <dd className="font-mono text-foreground break-all">{inspected.tokenEndpoint}</dd>
                                        <dt className="text-muted-foreground">Client auth</dt>
                                        <dd className="text-foreground">
                                            {inspected.tokenEndpointAuth
                                                ? AUTH_METHOD_LABEL[inspected.tokenEndpointAuth]
                                                : '—'}
                                        </dd>
                                        {inspected.scope && (
                                            <>
                                                <dt className="text-muted-foreground">Scope</dt>
                                                <dd className="font-mono text-foreground">{inspected.scope}</dd>
                                            </>
                                        )}
                                    </>
                                )}
                                <dt className="text-muted-foreground">Created</dt>
                                <dd className="text-foreground">{fmtDateTime(inspected.createdAt)}</dd>
                                <dt className="text-muted-foreground">Last updated</dt>
                                <dd className="text-foreground">{fmtDateTime(inspected.updatedAt)}</dd>
                            </dl>

                            {inspectedHint && (
                                <div className="flex gap-3 bg-hover p-3 rounded-lg text-subtitle">
                                    <TriangleAlert className="mt-0.5 size-4 text-amber-500 shrink-0" />
                                    <p>{inspectedHint}</p>
                                </div>
                            )}

                            {inspected.type === 'mcp_oauth' && (
                                <section className="space-y-3 pt-4 border-border border-t">
                                    <div className="flex justify-between items-center gap-3">
                                        <h4 className="font-medium text-foreground text-subtitle">
                                            Live validation
                                        </h4>
                                        <Button
                                variant="tertiary"
                                            disabled={validating === inspected.credentialId}
                                            onClick={() => validate(inspected.credentialId)}
                                        >
                                            {validating === inspected.credentialId ? (
                                                <Loader2 className="animate-spin" />
                                            ) : (
                                                'Validate now'
                                            )}
                                        </Button>
                                    </div>
                                    {inspectedValidation ? (
                                        <dl className="gap-x-6 gap-y-2 grid grid-cols-[auto_1fr] text-subtitle">
                                            <dt className="text-muted-foreground">Refresh exchange</dt>
                                            <dd className="text-foreground">
                                                {inspectedValidation.refreshStatus ?? 'not attempted'}
                                            </dd>
                                            <dt className="text-muted-foreground">MCP probe</dt>
                                            <dd className="text-foreground">
                                                {inspectedValidation.mcpProbeMethod
                                                    ? `failed at ${inspectedValidation.mcpProbeMethod}`
                                                    : inspectedValidation.status === 'valid'
                                                      ? 'ok'
                                                      : 'not reached'}
                                            </dd>
                                            <dt className="text-muted-foreground">Verdict</dt>
                                            <dd className="text-foreground">
                                                {inspectedValidation.status}
                                                {inspectedValidation.validatedAt &&
                                                    ` (${new Date(inspectedValidation.validatedAt).toLocaleTimeString()})`}
                                            </dd>
                                        </dl>
                                    ) : (
                                        <p className="text-muted-foreground text-subtitle">
                                            Not validated in this session.
                                        </p>
                                    )}
                                    {(
                                        [
                                            ['Token endpoint response', inspectedValidation?.refreshResponse],
                                            [
                                                `MCP server response${inspectedValidation?.mcpProbeMethod ? ` (${inspectedValidation.mcpProbeMethod})` : ''}`,
                                                inspectedValidation?.mcpProbeResponse
                                            ]
                                        ] as const
                                    )
                                        .filter(([, r]) => !!r)
                                        .map(([label, r]) => (
                                            <div key={label} className="space-y-1.5">
                                                <div className="text-caption text-muted-foreground">{label}</div>
                                                <pre className="bg-muted p-3 rounded-lg font-mono text-caption break-all whitespace-pre-wrap">
                                                    {`HTTP ${r!.statusCode}  ${r!.contentType}\n\n${r!.body}${r!.bodyTruncated ? '\n…(truncated)' : ''}`}
                                                </pre>
                                            </div>
                                        ))}
                                </section>
                            )}

                            {inspected.type === 'mcp_oauth' && editMode === 'secret' && (
                                <section className="space-y-4 pt-4 border-border border-t">
                                    <h4 className="font-medium text-foreground text-subtitle">
                                        Update client secret
                                    </h4>
                                    <Field label="New client secret">
                                        <Input
                                            type="password"
                                            autoComplete="new-password"
                                            value={secretForm.clientSecret}
                                            onChange={(e) =>
                                                setSecretForm((f) => ({ ...f, clientSecret: e.target.value }))
                                            }
                                        />
                                    </Field>
                                    <Field label="Client auth method" htmlFor="secret-auth-method">
                                        <Select
                                            value={secretForm.authMethod}
                                            onValueChange={(v) =>
                                                setSecretForm((f) => ({
                                                    ...f,
                                                    authMethod: v as Exclude<AuthMethod, 'none'>
                                                }))
                                            }
                                        >
                                            <SelectTrigger id="secret-auth-method">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="client_secret_basic">
                                                    {AUTH_METHOD_LABEL.client_secret_basic}
                                                </SelectItem>
                                                <SelectItem value="client_secret_post">
                                                    {AUTH_METHOD_LABEL.client_secret_post}
                                                </SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </Field>
                                    <FormActions>
                                        <Button variant="tertiary" onClick={() => setEditMode(null)}>
                                            Cancel
                                        </Button>
                                        <Button disabled={busy} onClick={() => saveSecret(inspected)}>
                                            Save and validate
                                        </Button>
                                    </FormActions>
                                </section>
                            )}

                            {inspected.type === 'mcp_oauth' && editMode === 'tokens' && (
                                <section className="space-y-4 pt-4 border-border border-t">
                                    <h4 className="font-medium text-foreground text-subtitle">Replace tokens</h4>
                                    <Field label="Access token">
                                        <Input
                                            type="password"
                                            autoComplete="new-password"
                                            value={tokenForm.accessToken}
                                            onChange={(e) =>
                                                setTokenForm((f) => ({ ...f, accessToken: e.target.value }))
                                            }
                                        />
                                    </Field>
                                    <Field label="Access token expires at">
                                        <Input
                                            type="datetime-local"
                                            value={tokenForm.expiresAt}
                                            onChange={(e) =>
                                                setTokenForm((f) => ({ ...f, expiresAt: e.target.value }))
                                            }
                                        />
                                    </Field>
                                    {inspected.hasRefresh && (
                                        <Field label="Refresh token">
                                            <Input
                                                type="password"
                                                autoComplete="new-password"
                                                value={tokenForm.refreshToken}
                                                onChange={(e) =>
                                                    setTokenForm((f) => ({ ...f, refreshToken: e.target.value }))
                                                }
                                            />
                                        </Field>
                                    )}
                                    <FormActions>
                                        <Button variant="tertiary" onClick={() => setEditMode(null)}>
                                            Cancel
                                        </Button>
                                        <Button disabled={busy} onClick={() => saveTokens(inspected)}>
                                            Save and validate
                                        </Button>
                                    </FormActions>
                                </section>
                            )}

                            <div className="flex flex-wrap gap-2 pt-4 border-border border-t">
                                {inspected.type === 'mcp_oauth' && inspected.hasRefresh && (
                                    <Button variant="tertiary" onClick={() => openEdit('secret', inspected)}>
                                        Update client secret
                                    </Button>
                                )}
                                {inspected.type === 'mcp_oauth' && (
                                    <Button variant="tertiary" onClick={() => openEdit('tokens', inspected)}>
                                        Replace tokens
                                    </Button>
                                )}
                                <Button
                                    variant="ghost-destructive"
                                                                        className="ml-auto"
                                    onClick={() => setArchiveTarget(inspected)}
                                >
                                    Archive
                                </Button>
                            </div>
                        </>
                    )}
                </SheetContent>
            </Sheet>

            <Sheet open={wizardStep > 0} onOpenChange={(o) => !o && setWizardStep(0)}>
                <SheetContent className="space-y-6 sm:max-w-md overflow-y-auto">
                    <SheetHeader>
                        <SheetTitle>New MCP OAuth credential</SheetTitle>
                        <SheetDescription>
                            Step {wizardStep} of 3 · Adding to {vault?.name}
                        </SheetDescription>
                    </SheetHeader>

                    {wizardStep === 1 && (
                        <div className="space-y-4">
                            <Field
                                label="MCP server URL"
                                hint="Must match the URL on the agent's MCP server exactly (path and subdomain)."
                            >
                                <Input
                                    placeholder="https://mcp.example.com/mcp"
                                    value={wizard.mcpServerUrl}
                                    onChange={(e) => setWizard((w) => ({ ...w, mcpServerUrl: e.target.value }))}
                                    className="font-mono"
                                />
                            </Field>
                            <Field label="Display name (optional)">
                                <Input
                                    value={wizard.displayName}
                                    onChange={(e) => setWizard((w) => ({ ...w, displayName: e.target.value }))}
                                />
                            </Field>
                            <FormActions>
                                <Button disabled={!wizardStep1Valid} onClick={() => setWizardStep(2)}>
                                    Next
                                </Button>
                            </FormActions>
                        </div>
                    )}

                    {wizardStep === 2 && (
                        <div className="space-y-4">
                            <Field label="Access token">
                                <Input
                                    type="password"
                                    autoComplete="new-password"
                                    value={wizard.accessToken}
                                    onChange={(e) => setWizard((w) => ({ ...w, accessToken: e.target.value }))}
                                />
                            </Field>
                            <Field label="Access token expires at (optional)">
                                <Input
                                    type="datetime-local"
                                    value={wizard.expiresAt}
                                    onChange={(e) => setWizard((w) => ({ ...w, expiresAt: e.target.value }))}
                                />
                            </Field>
                            <SettingRow
                                label="Auto-refresh"
                                htmlFor="wizard-auto-refresh"
                                wholeRowClickable
                                description="Renew the access token automatically with a refresh token"
                                compact
                                className="py-2 border-b-0"
                            >
                                <Switch
                                    id="wizard-auto-refresh"
                                    checked={wizard.withRefresh}
                                    onCheckedChange={(checked) => setWizard((w) => ({ ...w, withRefresh: checked }))}
                                />
                            </SettingRow>
                            {wizard.withRefresh && (
                                <div className="space-y-4 pt-4 border-border border-t">
                                    <Field label="Token endpoint">
                                        <Input
                                            placeholder="https://<keycloak>/realms/<realm>/protocol/openid-connect/token"
                                            value={wizard.tokenEndpoint}
                                            onChange={(e) => setWizard((w) => ({ ...w, tokenEndpoint: e.target.value }))}
                                            className="font-mono"
                                        />
                                    </Field>
                                    <Field label="Client ID">
                                        <Input
                                            value={wizard.clientId}
                                            onChange={(e) => setWizard((w) => ({ ...w, clientId: e.target.value }))}
                                            className="font-mono"
                                        />
                                    </Field>
                                    <Field label="Client auth method" htmlFor="wizard-auth-method">
                                        <Select
                                            value={wizard.authMethod}
                                            onValueChange={(v) => setWizard((w) => ({ ...w, authMethod: v as AuthMethod }))}
                                        >
                                            <SelectTrigger id="wizard-auth-method">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {(Object.keys(AUTH_METHOD_LABEL) as AuthMethod[]).map((m) => (
                                                    <SelectItem key={m} value={m}>
                                                        {AUTH_METHOD_LABEL[m]}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </Field>
                                    {wizard.authMethod !== 'none' && (
                                        <Field label="Client secret">
                                            <Input
                                                type="password"
                                                autoComplete="new-password"
                                                value={wizard.clientSecret}
                                                onChange={(e) => setWizard((w) => ({ ...w, clientSecret: e.target.value }))}
                                            />
                                        </Field>
                                    )}
                                    <Field label="Refresh token">
                                        <Input
                                            type="password"
                                            autoComplete="new-password"
                                            value={wizard.refreshToken}
                                            onChange={(e) => setWizard((w) => ({ ...w, refreshToken: e.target.value }))}
                                        />
                                    </Field>
                                    <Field label="Scope (optional)">
                                        <Input
                                            value={wizard.scope}
                                            onChange={(e) => setWizard((w) => ({ ...w, scope: e.target.value }))}
                                            className="font-mono"
                                        />
                                    </Field>
                                </div>
                            )}
                            <FormActions>
                                <Button variant="tertiary" onClick={() => setWizardStep(1)}>
                                    Back
                                </Button>
                                <Button disabled={!wizardStep2Valid} onClick={() => setWizardStep(3)}>
                                    Next
                                </Button>
                            </FormActions>
                        </div>
                    )}

                    {wizardStep === 3 && (
                        <div className="space-y-4">
                            <dl className="gap-x-6 gap-y-2 grid grid-cols-[auto_1fr] text-subtitle">
                                <dt className="text-muted-foreground">MCP server</dt>
                                <dd className="font-mono text-foreground break-all">{wizard.mcpServerUrl}</dd>
                                <dt className="text-muted-foreground">Name</dt>
                                <dd className="text-foreground">{wizard.displayName || wizard.mcpServerUrl}</dd>
                                <dt className="text-muted-foreground">Expires</dt>
                                <dd className="text-foreground">
                                    {wizard.expiresAt ? new Date(wizard.expiresAt).toLocaleString() : 'Not set'}
                                </dd>
                                <dt className="text-muted-foreground">Auto-refresh</dt>
                                <dd className="text-foreground">
                                    {wizard.withRefresh
                                        ? `${wizard.clientId} · ${AUTH_METHOD_LABEL[wizard.authMethod]}`
                                        : 'Off'}
                                </dd>
                                <dt className="text-muted-foreground">Affects</dt>
                                <dd className="text-foreground">{usedBy.join(', ') || 'No agents yet'}</dd>
                            </dl>
                            <FormActions>
                                <Button variant="tertiary" onClick={() => setWizardStep(2)}>
                                    Back
                                </Button>
                                <Button disabled={busy} onClick={createCredential}>
                                    {busy ? <Loader2 className="animate-spin" /> : 'Create'}
                                </Button>
                            </FormActions>
                        </div>
                    )}
                </SheetContent>
            </Sheet>

            <ConfirmDeleteDialog
                open={!!archiveTarget}
                onOpenChange={(o) => !o && setArchiveTarget(null)}
                onConfirm={archive}
                title="Archive credential?"
                description={`'${archiveTarget?.displayName}' will be purged from the vault. Agents using this vault lose access to the MCP server immediately, and this cannot be undone.`}
                confirmLabel="Archive"
                pendingLabel="Archiving..."
            />
        </section>
    );
};
